import { useMemo } from 'react';
import {
  QuestionAnsweredRecap,
  type QuestionRecapPair,
} from './QuestionAnsweredRecap';

/** Reads the ```question fence the assistant used to write before `ask_user_question` existed.
 *
 * Read-only by design: these questions were asked and answered in the past, so they render as the
 * questions alone and the user's own reply below stays the record of what they chose. It exists so
 * scrolling back through an older conversation does not turn its cards into raw JSON. Delete it
 * once no stored conversation carries a fence. */

interface LegacyFenceQuestion {
  question: string;
}

function parseLegacyQuestions(code: string): LegacyFenceQuestion[] {
  try {
    const parsed: unknown = JSON.parse(code);
    if (typeof parsed !== 'object' || parsed === null) return [];

    const block = parsed as Record<string, unknown>;
    if (typeof block.title === 'string') return [{ question: block.title }];
    if (typeof block.prompt === 'string') return [{ question: block.prompt }];
    if (!Array.isArray(block.questions)) return [];

    return block.questions.flatMap((entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as Record<string, unknown>).question === 'string'
        ? [{ question: (entry as Record<string, string>).question }]
        : []
    );
  } catch {
    return [];
  }
}

export function QuestionBlockRenderer({
  code,
  isIncomplete,
}: {
  code: string;
  isIncomplete: boolean;
}) {
  const pairs = useMemo<QuestionRecapPair[]>(
    () =>
      parseLegacyQuestions(code).map((question) => ({
        question: question.question,
        answer: '',
      })),
    [code]
  );

  // A fence still streaming, or one that will not parse, falls through to the ordinary code block
  // rather than rendering an empty card.
  if (isIncomplete || pairs.length === 0)
    return (
      <pre className="my-sm overflow-x-auto rounded-lg border-[0.5px] border-border-tertiary bg-bg-secondary p-lg text-xs text-text-secondary">
        <code>{code}</code>
      </pre>
    );

  return <QuestionAnsweredRecap pairs={pairs} />;
}
